import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { DATA_TOKENS } from '../utils/data-tokens'

export type DataModuleSkeletonVariant = 'dashboard' | 'table'

const KPI_KEYS = ['kpi-a', 'kpi-b'] as const
const CHART_PAIR_KEYS = ['a', 'b'] as const
const TABLE_ROW_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8', 'r9', 'r10'] as const

const CARD = 'rounded-xl border border-neutral-200 bg-white'
const BAR = 'rounded bg-neutral-200/80'
const BAR_SOFT = 'rounded bg-neutral-100'
const ROW_DIVIDER = 'border-[#eff2f7]'

function FiltersRow(): ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className={`h-9 w-44 ${BAR}`} />
      <div className={`h-9 w-28 ${BAR_SOFT}`} />
      <div className={`ml-auto h-9 w-28 ${BAR_SOFT}`} />
    </div>
  )
}

function ChartCard({ tall }: { tall?: boolean }): ReactElement {
  return (
    <div className={`${CARD} p-5`}>
      <div className={`h-4 w-40 ${BAR}`} />
      <div className={`mt-2 h-3 w-56 ${BAR_SOFT}`} />
      <div className={`mt-5 w-full ${tall ? 'h-56' : 'h-40'} rounded-lg bg-neutral-100`} />
    </div>
  )
}

function DashboardSkeleton(): ReactElement {
  return (
    <>
      <FiltersRow />
      <section className="grid gap-3 md:grid-cols-2">
        {KPI_KEYS.map((key) => (
          <div key={key} className={`${CARD} p-5`}>
            <div className={`h-3 w-24 ${BAR_SOFT}`} />
            <div className={`mt-3 h-8 w-32 ${BAR}`} />
          </div>
        ))}
      </section>
      <section className="grid gap-3 lg:grid-cols-2">
        {CHART_PAIR_KEYS.map((key) => (
          <ChartCard key={`sov-${key}`} />
        ))}
      </section>
      <section className="grid gap-3 lg:grid-cols-2">
        {CHART_PAIR_KEYS.map((key) => (
          <ChartCard key={`trend-${key}`} />
        ))}
      </section>
      <ChartCard tall />
      <section className="grid gap-3 lg:grid-cols-2">
        {CHART_PAIR_KEYS.map((key) => (
          <ChartCard key={`traits-${key}`} tall />
        ))}
      </section>
    </>
  )
}

function TableSkeleton(): ReactElement {
  return (
    <>
      <div className={`rounded-[24px] border ${DATA_TOKENS.border} bg-white/90 p-4`}>
        <FiltersRow />
      </div>
      <div className={`overflow-hidden rounded-[28px] border ${DATA_TOKENS.border} bg-white`}>
        <div className={`h-11 border-b ${ROW_DIVIDER} ${DATA_TOKENS.panelMuted}`} />
        {TABLE_ROW_KEYS.map((key) => (
          <div
            key={key}
            className={`flex items-center gap-3 border-t ${ROW_DIVIDER} px-4 py-3.5`}
          >
            <div className={`h-3 max-w-[55%] flex-1 ${BAR_SOFT}`} />
            <div className={`h-3 w-20 ${BAR_SOFT}`} />
            <div className={`h-3 w-16 ${BAR_SOFT}`} />
          </div>
        ))}
      </div>
    </>
  )
}

export function DataModuleSkeleton({
  variant,
}: {
  variant: DataModuleSkeletonVariant
}): ReactElement {
  const { t } = useTranslation()
  const label = <span className="sr-only">{t('data.loading')}</span>

  if (variant === 'table') {
    return (
      <div className={`flex h-full min-h-0 flex-col px-4 py-6 md:px-6 ${DATA_TOKENS.page}`}>
        <div
          role="status"
          aria-busy="true"
          aria-live="polite"
          className="mx-auto flex w-full max-w-[1440px] flex-1 animate-pulse flex-col gap-3 overflow-hidden"
        >
          {label}
          <TableSkeleton />
        </div>
      </div>
    )
  }

  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className="flex min-h-screen animate-pulse flex-col gap-3 bg-dashboard px-4 py-6 md:px-6 lg:px-8"
    >
      {label}
      <DashboardSkeleton />
    </div>
  )
}
